import { useCallback, useEffect, useState } from "react";
import { MessageSquarePlus, Send } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dataHoraBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, Field, Loading, Surface } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NOTICE_META } from "@/components/partner/NoticeBell";

const TYPES = Object.entries(NOTICE_META).map(([value, m]) => ({ value, label: m.label }));
const ACTIONS = [
  { value: "", label: "Sem ação" },
  { value: "repor|Ver pedido", label: "Ver pedido" },
  { value: "estoque|Registrar vendas", label: "Registrar vendas" },
  { value: "estoque|Ver meu estoque", label: "Ver meu estoque" },
  { value: "fechamentos|Ver fechamento", label: "Ver fechamento" },
  { value: "repor|Ver produtos", label: "Ver produtos" },
];

export function NoticesPanel({ partners }) {
  const [notices, setNotices] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notices");
      setNotices(data);
    } catch (err) {
      toast.error(apiError(err));
      setNotices([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (notices === null) return <Loading label="Carregando comunicação..." />;

  return (
    <div className="space-y-4" data-testid="admin-notices-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Avisos enviados aos parceiros. Os automáticos (reposição pronta, fechamento) também
          ficam registrados aqui.
        </p>
        <Button
          onClick={() => setCreating(true)}
          data-testid="new-notice-btn"
          className="h-10 shrink-0 gap-2 rounded-full px-4 font-semibold"
        >
          <MessageSquarePlus className="h-4 w-4" /> Nova mensagem
        </Button>
      </div>

      {notices.length === 0 ? (
        <EmptyState
          icon={MessageSquarePlus}
          title="Nenhuma mensagem enviada"
          description="Envie avisos, dicas e lembretes para um parceiro ou para todos."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {notices.map((n) => {
            const meta = NOTICE_META[n.type] || NOTICE_META.GERAL;
            const Icon = meta.icon;
            return (
              <Surface
                key={n.id}
                className="min-w-0 overflow-hidden p-4"
                data-testid={`admin-notice-${n.id}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase ${meta.cls}`}
                  >
                    <Icon className="h-3 w-3" /> {meta.label}
                  </span>
                  {n.source === "AUTO" && (
                    <span className="rounded-full border border-white/15 px-2 py-0.5 text-[10px] uppercase text-muted-foreground">
                      automático
                    </span>
                  )}
                  <span className="ml-auto font-num text-[10px] text-muted-foreground">
                    {dataHoraBR(n.created_at)}
                  </span>
                </div>
                <p className="mt-2 text-sm font-medium">{n.title}</p>
                <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{n.body}</p>
                <div className="mt-3 space-y-1 border-t border-white/10 pt-3 text-[11px] text-muted-foreground">
                  <p data-testid={`notice-recipients-${n.id}`}>
                    Destinatários: {n.recipient_names || "—"} ({n.recipients})
                  </p>
                  <p data-testid={`notice-reads-${n.id}`}>
                    <span className="font-num">
                      {n.reads}/{n.recipients}
                    </span>{" "}
                    visualizaram · {n.unread} não lido(s)
                  </p>
                  {n.action?.label && <p>Ação: {n.action.label}</p>}
                  {n.created_by && <p>Por: {n.created_by}</p>}
                </div>
              </Surface>
            );
          })}
        </div>
      )}

      {creating && (
        <NoticeModal
          partners={partners}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function NoticeModal({ partners, onClose, onSaved }) {
  const active = partners.filter((p) => p.active !== false);
  const [type, setType] = useState("AVISO");
  const [audience, setAudience] = useState("ALL");
  const [ids, setIds] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [action, setAction] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const count = audience === "ALL" ? active.length : ids.length;

  const toggle = (id) =>
    setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const send = async () => {
    const [target, label] = action ? action.split("|") : [null, null];
    setBusy(true);
    try {
      await api.post("/notices", {
        type,
        title,
        body,
        audience,
        partner_ids: audience === "ALL" ? [] : ids,
        action_label: label,
        action_target: target,
      });
      toast.success(`Mensagem enviada para ${count} parceiro(s).`);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error("Preencha título e mensagem.");
      return;
    }
    if (count === 0) {
      toast.error("Selecione ao menos um parceiro.");
      return;
    }
    setConfirming(true);
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Nova mensagem"
      description="Vai direto para a central de avisos do parceiro."
      testid="notice-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <Field label="Tipo">
          <NativeSelect
            value={type}
            onChange={setType}
            options={TYPES}
            data-testid="notice-type-select"
          />
        </Field>

        <Field label="Destinatários">
          <NativeSelect
            value={audience}
            onChange={(v) => {
              setAudience(v);
              setIds([]);
            }}
            options={[
              { value: "ALL", label: `Todos os parceiros (${active.length})` },
              { value: "SELECTED", label: "Selecionar parceiros" },
            ]}
            data-testid="notice-audience-select"
          />
        </Field>

        {audience === "SELECTED" && (
          <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-white/10 p-2">
            {active.map((p) => (
              <label
                key={p.id}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-white/5"
              >
                <input
                  type="checkbox"
                  checked={ids.includes(p.id)}
                  onChange={() => toggle(p.id)}
                  data-testid={`notice-partner-${p.id}`}
                  className="h-4 w-4 accent-primary"
                />
                <span className="truncate">{p.name}</span>
              </label>
            ))}
          </div>
        )}

        <Field label="Título">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={140}
            data-testid="notice-title-input"
          />
        </Field>
        <Field label="Mensagem">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            data-testid="notice-body-input"
          />
        </Field>
        <Field label="Ação (opcional)">
          <NativeSelect
            value={action}
            onChange={setAction}
            options={ACTIONS}
            data-testid="notice-action-select"
          />
        </Field>

        {confirming ? (
          <div
            className="rounded-lg border border-primary/40 bg-primary/10 p-3"
            data-testid="notice-confirm-box"
          >
            <p className="text-sm">Enviar esta mensagem para {count} parceiro(s)?</p>
            <div className="mt-3 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setConfirming(false)}
                className="rounded-full"
                data-testid="notice-confirm-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={send}
                disabled={busy}
                className="gap-1.5 rounded-full"
                data-testid="notice-confirm-send-btn"
              >
                <Send className="h-3.5 w-3.5" /> {busy ? "Enviando..." : "Enviar"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end gap-2 border-t border-white/10 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-full"
              data-testid="notice-cancel-btn"
            >
              Cancelar
            </Button>
            <Button type="submit" className="rounded-full" data-testid="notice-submit-btn">
              Enviar mensagem
            </Button>
          </div>
        )}
      </form>
    </ResponsiveModal>
  );
}
